import { useMemo, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export interface DeepLinkParams {
  route: string;
  projectName?: string;
  fiscalYearQuarter?: string;
}

export interface RouteChangeMessage {
  type: 'RouteChange';
  route: string;
  params?: {
    projectName?: string;
    fiscalYearQuarter?: string;
  };
}

export interface NavigateToRouteMessage {
  type: 'NavigateToRoute';
  route: string;
}

const EVO_PLATFORM_ORIGINS = [
  'https://go.accessacloud.com',
  'https://preprodgo.accessacloud.com',
  'https://testgo.accessacloud.com',
  'https://demogo.accessacloud.com',
];

const _initialSearchParams = new URLSearchParams(window.location.search);
const _initialWorkspaceId = _initialSearchParams.get('workspaceId') || '';
const _initialInstanceId = _initialSearchParams.get('instanceId') || '';

export function getInitialWorkspaceId(): string {
  return _initialWorkspaceId;
}

export function getInitialInstanceId(): string {
  return _initialInstanceId;
}

export function getEvoPlatformBaseUrl(workspaceUrl?: string): string {
  if (workspaceUrl) return workspaceUrl;

  const hostname = window.location.hostname;
  const port = window.location.port;
  const devPorts = ['4792', '5173', '6006', '6007', '3000'];
  if (hostname === 'localhost' && devPorts.includes(port)) {
    return 'http://localhost:4792';
  }

  const origin = window.location.origin;
  if (origin.includes('preprod')) return 'https://preprodgo.accessacloud.com';
  if (origin.includes('test')) return 'https://testgo.accessacloud.com';
  if (origin.includes('demo')) return 'https://demogo.accessacloud.com';

  return 'https://go.accessacloud.com';
}

export function parseDeepLinkParams(): DeepLinkParams {
  const searchParams = new URLSearchParams(window.location.search);
  const hash = window.location.hash;
  
  let route = searchParams.get('route') || '/';
  const projectName = searchParams.get('projectName') || undefined;
  const fiscalYearQuarter = searchParams.get('fiscalYearQuarter') || undefined;
  
  if (hash && hash.startsWith('#/')) {
    route = hash.substring(1);
  }
  
  if (projectName && fiscalYearQuarter && route === '/') {
    route = `/review/${encodeURIComponent(projectName)}/${encodeURIComponent(fiscalYearQuarter)}`;
  }
  
  return {
    route,
    projectName,
    fiscalYearQuarter,
  };
}

export function notifyParentRouteChange(route: string, params?: DeepLinkParams): void {
  const message: RouteChangeMessage = {
    type: 'RouteChange',
    route,
    params: params ? {
      projectName: params.projectName,
      fiscalYearQuarter: params.fiscalYearQuarter,
    } : undefined,
  };

  EVO_PLATFORM_ORIGINS.forEach(origin => {
    try {
      window.parent.postMessage(JSON.stringify(message), origin);
    } catch (err) {
      console.debug(`ProjectApproval_ Failed to post route change to ${origin}:`, err);
    }
  });
}

export function useDeepLink(): DeepLinkParams {
  return useMemo(() => parseDeepLinkParams(), []);
}

export function useRouteSync(): void {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const reviewMatch = location.pathname.match(/^\/review\/([^/]+)\/([^/]+)$/);
    
    if (reviewMatch) {
      const params: DeepLinkParams = {
        route: location.pathname,
        projectName: decodeURIComponent(reviewMatch[1]),
        fiscalYearQuarter: decodeURIComponent(reviewMatch[2]),
      };
      notifyParentRouteChange(location.pathname, params);
    } else {
      notifyParentRouteChange(location.pathname);
    }
  }, [location.pathname]);

  const handleMessage = useCallback((event: MessageEvent) => {
    if (!EVO_PLATFORM_ORIGINS.includes(event.origin)) {
      return;
    }

    try {
      const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      
      if (data.type === 'NavigateToRoute' && data.route) {
        console.log('ProjectApproval_ Received navigation request from parent:', data.route);
        navigate(data.route);
      }
    } catch (err) {
      console.debug('ProjectApproval_ Failed to parse message from parent:', err);
    }
  }, [navigate]);

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);
}

export function buildDeepLinkUrl(
  baseUrl: string,
  params: Partial<DeepLinkParams>
): string {
  const url = new URL(baseUrl);
  
  if (params.route && params.route !== '/') {
    url.searchParams.set('route', params.route);
  }
  if (params.projectName) {
    url.searchParams.set('projectName', params.projectName);
  }
  if (params.fiscalYearQuarter) {
    url.searchParams.set('fiscalYearQuarter', params.fiscalYearQuarter);
  }
  
  return url.toString();
}
